import { gql, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useEventsSocket } from '../../../store';
import type { ActivityCategory, ActivityLog } from './types';

/* Loads the newest 100 entries when the event opens, the most the API returns in one page. */
const EVENT_ACTIVITY_LOGS = gql`
  query EventActivityLogs($eventId: Int!) {
    getActivityLogs(filters: { eventId: $eventId }, pagination: { count: 100 }) {
      logs {
        id
        category
        title
        detail
        source
        timestamp
      }
      total
    }
  }
`;

interface StoredLog {
  id: number | string;
  category: ActivityCategory;
  title: string;
  detail?: string | null;
  source: string;
  timestamp: string;
}

interface QueryResult {
  getActivityLogs?: { logs: StoredLog[]; total: number } | null;
}

interface QueryVars {
  eventId: number;
}

export interface EventActivity {
  logs: ActivityLog[];
  /* First load of the stored history; live entries can already be in `logs`. */
  loading: boolean;
  /* The stored history could not be loaded; live entries still arrive. */
  error: boolean;
  retry: () => void;
}

const toActivityLog = (log: StoredLog): ActivityLog => ({
  id: String(log.id),
  category: log.category,
  title: log.title,
  detail: log.detail ?? undefined,
  source: log.source,
  timestamp: log.timestamp
});

/*
 * Activity log entries for one event: the stored history plus what arrives on the events socket while the
 * page is open. A stored entry is broadcast with its stored id, so the two lists overlap only by id. Entries
 * that are only broadcast (never stored) are gone after a reload. The history is reloaded when the socket
 * reconnects, since entries stored while it was down never arrived live.
 */
// `enabled: false` loads and listens to nothing (the tab is hidden).
export const useEventActivity = (eventId: number, enabled = true): EventActivity => {
  const { subscribe, subscribeActivity } = useEventsSocket();
  const [live, setLive] = useState<ActivityLog[]>([]);

  const { data, loading, error, refetch } = useQuery<QueryResult, QueryVars>(EVENT_ACTIVITY_LOGS, {
    variables: { eventId },
    skip: !enabled || Number.isNaN(eventId),
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    setLive([]);

    if (!enabled) {
      return undefined;
    }

    return subscribeActivity((logEventId, log) => {
      if (logEventId !== eventId) {
        return;
      }

      setLive((current) => (current.some((entry) => entry.id === log.id) ? current : [log, ...current]));
    });
  }, [eventId, enabled, subscribeActivity]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return subscribe(`activity:${eventId}`, (message) => {
      if (message.kind === 'resync') {
        void refetch();
      }
    });
  }, [eventId, enabled, subscribe, refetch]);

  const logs = useMemo(() => {
    const byId = new Map<string, ActivityLog>();

    for (const log of data?.getActivityLogs?.logs ?? []) {
      byId.set(String(log.id), toActivityLog(log));
    }

    for (const log of live) {
      if (!byId.has(log.id)) {
        byId.set(log.id, log);
      }
    }

    return [...byId.values()];
  }, [data, live]);

  return {
    logs,
    loading: loading && !data,
    error: Boolean(error),
    retry: (): void => {
      void refetch();
    }
  };
};
